/**
 * Apple Contacts via Capacitor bridge. Native iOS only. The web build ships
 * a vCard upload fallback so non-Capacitor environments still get a path.
 *
 * v0: Capacitor bridge is a thin interface contract -- the actual native
 * plugin lives in the iOS Capacitor shell (empire-apps-chassis ships TWA +
 * Capacitor templates per fork-5).
 */
import type { ContactSeed, Provenance } from './types.ts';

interface CapacitorContactsPluginShape {
  getPermissions?(): Promise<{ contacts: 'granted' | 'denied' | 'prompt' }>;
  requestPermissions?(): Promise<{ contacts: 'granted' | 'denied' }>;
  getContacts?(projection: {
    name?: boolean;
    phones?: boolean;
    emails?: boolean;
    image?: boolean;
    birthday?: boolean;
    organization?: boolean;
    note?: boolean;
  }): Promise<{ contacts: ReadonlyArray<CapContact> }>;
}

interface CapContact {
  contactId?: string;
  name?: { display?: string };
  phones?: ReadonlyArray<{ number: string; type?: string }>;
  emails?: ReadonlyArray<{ address: string; type?: string }>;
  image?: { base64String?: string };
  birthday?: string;
  organization?: { company?: string };
  note?: string;
}

function getPlugin(): CapacitorContactsPluginShape | null {
  const cap = (globalThis as { Capacitor?: { Plugins?: Record<string, unknown> } }).Capacitor;
  return (cap?.Plugins?.Contacts as CapacitorContactsPluginShape | undefined) ?? null;
}

export async function fetchAppleContactsNative(): Promise<readonly ContactSeed[]> {
  const plugin = getPlugin();
  if (!plugin?.getContacts) throw new Error('not_on_capacitor');

  const perm = await plugin.getPermissions?.();
  if (perm?.contacts !== 'granted') {
    const req = await plugin.requestPermissions?.();
    if (req?.contacts !== 'granted') throw new Error('permission_denied');
  }
  const { contacts } = await plugin.getContacts({ name: true, phones: true, emails: true, image: true, birthday: true, organization: true });
  return contacts.map(toSeed).filter(notNull);
}

function notNull<T>(x: T | null): x is T { return x !== null; }

function toSeed(c: CapContact): ContactSeed | null {
  const name = c.name?.display?.trim();
  if (!name) return null;
  const phones = (c.phones ?? []).map((p) => p.number).filter(Boolean);
  const emails = (c.emails ?? []).map((e) => e.address).filter(Boolean);
  const photo = c.image?.base64String ? `data:image/jpeg;base64,${c.image.base64String}` : undefined;
  const relation_hint = c.organization?.company ? 'work' : undefined;
  const provenance: Provenance = {
    connector: 'apple-contacts',
    source_id: c.contactId ?? name,
    fetched_at: Date.now(),
  };
  return {
    external_id: c.contactId ?? `apple-${name}`,
    name,
    phones,
    emails,
    ...(photo ? { photo_url: photo } : {}),
    ...(c.birthday ? { birthday: c.birthday } : {}),
    ...(relation_hint ? { relation_hint } : {}),
    source: 'apple-contacts',
    provenance,
  };
}

/** vCard upload fallback for web. Returns ContactSeed[] parsed from VCF. */
export async function parseVcard(blob: Blob): Promise<readonly ContactSeed[]> {
  const text = await blob.text();
  const cards = text.split(/BEGIN:VCARD/i).slice(1);
  const out: ContactSeed[] = [];
  let idx = 0;
  for (const raw of cards) {
    const body = raw.split(/END:VCARD/i)[0] ?? '';
    const name = matchFirst(body, /^FN(?:;[^:]*)?:(.+)$/im);
    if (!name) continue;
    const phones = matchAll(body, /^TEL(?:;[^:]*)?:(.+)$/gim);
    const emails = matchAll(body, /^EMAIL(?:;[^:]*)?:(.+)$/gim);
    const bday = matchFirst(body, /^BDAY(?:;[^:]*)?:(.+)$/im);
    const org = matchFirst(body, /^ORG(?:;[^:]*)?:(.+)$/im);
    idx += 1;
    out.push({
      external_id: `vcard-${idx}`,
      name: name.trim(),
      phones,
      emails,
      ...(bday ? { birthday: normaliseBday(bday) } : {}),
      ...(org ? { relation_hint: 'work' } : {}),
      source: 'apple-contacts',
      provenance: { connector: 'apple-contacts', source_id: `vcard-${idx}`, fetched_at: Date.now() },
    });
  }
  return out;
}

function matchFirst(body: string, re: RegExp): string | null {
  const m = body.match(re);
  return m && m[1] ? m[1].trim() : null;
}
function matchAll(body: string, re: RegExp): string[] {
  const out: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) if (m[1]) out.push(m[1].trim());
  return out;
}
function normaliseBday(s: string): string {
  const m = s.match(/^(\d{4})-?(\d{2})-?(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : s;
}
